import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

export type CarrierDiscountFunding = "PLATFORM" | "CARRIER";

export type CarrierDiscountRuleStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";
export type CarrierDiscountStackPolicy = "STACK" | "EXCLUSIVE";

export interface CarrierLabelDiscountEligibility {
  id: string;
  customerId: string | null;
  carrierCustomerTierId: string | null;
  carrierServiceId: string | null;
  dispatchCenterId: string | null;
}

export interface CarrierLabelDiscountRule {
  funding: CarrierDiscountFunding;
  id: string;
  carrierId: string;
  name: string;
  status: CarrierDiscountRuleStatus;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  ruleId: string;
  chargeScope: "LABEL";
  discountType: "PERCENTAGE";
  percentage: number;
  stackPolicy: CarrierDiscountStackPolicy;
  eligibilityRows: CarrierLabelDiscountEligibility[];
}

export interface CarrierLabelDiscountRuleInput {
  funding?: CarrierDiscountFunding;
  carrierId: string;
  name?: string;
  status?: CarrierDiscountRuleStatus;
  startsAt?: string | null;
  endsAt?: string | null;
  percentage?: number;
  stackPolicy?: CarrierDiscountStackPolicy;
  eligibilityRows?: Array<{
    customerId?: string | null;
    carrierCustomerTierId?: string | null;
    carrierServiceId?: string | null;
    dispatchCenterId?: string | null;
  }>;
}

const rulesKey = (carrierId: string | null) => [
  "admin-carrier-discount-rules",
  carrierId ?? "",
];

function authHeaders(token: string | null | undefined) {
  return { Authorization: `Bearer ${token}` };
}

export function useAdminCarrierLabelDiscountRules(carrierId: string | null) {
  const { token } = useSession();
  return useQuery({
    queryKey: rulesKey(carrierId),
    queryFn: async () => {
      const { data } = await axios.get<{ data: CarrierLabelDiscountRule[] }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/discount-rules`,
        { headers: authHeaders(token) }
      );
      return data.data;
    },
    enabled: !!token && !!carrierId,
  });
}

export function useCreateAdminCarrierLabelDiscountRule() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      ...body
    }: CarrierLabelDiscountRuleInput) => {
      const { data } = await axios.post<{ data: CarrierLabelDiscountRule }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/discount-rules`,
        body,
        { headers: authHeaders(token) }
      );
      return data.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: rulesKey(variables.carrierId),
      });
    },
  });
}

export function useUpdateAdminCarrierLabelDiscountRule() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      id,
      ...body
    }: CarrierLabelDiscountRuleInput & { id: string }) => {
      const { data } = await axios.patch<{ data: CarrierLabelDiscountRule }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/discount-rules/${id}`,
        body,
        { headers: authHeaders(token) }
      );
      return data.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: rulesKey(variables.carrierId),
      });
    },
  });
}

export function useDeleteAdminCarrierLabelDiscountRule() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      id,
    }: {
      carrierId: string;
      id: string;
    }) => {
      await axios.delete(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/discount-rules/${id}`,
        { headers: authHeaders(token) }
      );
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: rulesKey(variables.carrierId),
      });
    },
  });
}
